// import Box from "@mui/material/Box";
// import isString from "lodash/isString";
// import PropTypes from "prop-types";
// import { useCallback, useMemo } from "react";
// import { NavLink, useLocation } from "react-router-dom";

// const LoanDrilldownRouterTabSwitcher = ({
//   tabs,
//   baseUrl = "/loan/",
//   orientation = "horizontal",
//   variant = "background",
//   underlinePosition = "bottom",
// }) => {
//   const location = useLocation();

//   const currentPath = useMemo(
//     () => location.pathname.replace(/&/g, "and").replace(/\/$/, ""),
//     [location.pathname],
//   );

//   const cleanBase = useMemo(
//     () => baseUrl.replace(/&/g, "and").replace(/\/$/, ""),
//     [baseUrl],
//   );

//   const styles = useMemo(() => {
//     return {
//       display: "flex",
//       flexDirection: orientation === "horizontal" ? "row" : "column",
//       ".nav-link": {
//         fontFamily: "var(--ibm)",
//         color: "#181d1f",
//         fontWeight: 400,
//         fontSize: "11px",
//         // fontSize: "13px",
//         p: 1.5,
//         borderRadius: 0,
//         whiteSpace: "nowrap",
//         textDecoration: "none",
//         transition: "all 0.2s ease",

//         ...(orientation === "vertical" && {
//           whiteSpace: "nowrap",
//           minWidth: "fit-content",
//           justifyContent: "flex-start",
//           textAlign: "left",
//           borderLeft: "2px solid transparent",
//         }),

//         "&.active": {
//           color: "#2196B5",
//           fontWeight: 700,
//           borderBottom: "2px solid #2196B5",

//           ...(variant === "underline"
//             ? {
//                 background: "none",
//                 borderRadius: "0 !important",
//                 fontWeight: 500,
//               }
//             : {
//                 background: "#2196B514",
//               }),

//           ...(variant === "underline" && {
//             ...(orientation === "horizontal" &&
//               underlinePosition === "bottom" && {
//                 borderBottom: "2px solid #2196B5",
//               }),
//             ...(orientation === "horizontal" &&
//               underlinePosition === "top" && {
//                 borderTop: "2px solid #2196B5",
//               }),
//             ...(orientation === "vertical" &&
//               underlinePosition === "left" && {
//                 borderLeft: "2px solid #2196B5",
//               }),
//             ...(orientation === "vertical" &&
//               underlinePosition === "right" && {
//                 borderRight: "2px solid #2196B5",
//               }),
//           }),
//         },
//       },
//     };
//   }, [orientation, variant, underlinePosition]);

//   const isTabActive = useCallback(
//     (tabKey, linkActive) => {
//       const isSummary = tabKey === "summary";

//       if (isSummary) {
//         return [cleanBase, `${cleanBase}/summary`].includes(currentPath);
//       }

//       // Automatically active if tabKey appears as a segment anywhere in the path
//       // Example matches:
//       //   /loan/1001/fees-and-closing/wire-instructions -> tabKey "fees-and-closing" or "wire-instructions"
//       const segmentRegex = new RegExp(`(^|/)${tabKey}(/|$)`);
//       return (
//         (currentPath.startsWith(cleanBase) &&
//           segmentRegex.test(currentPath.replace(cleanBase, ""))) || // only look after base
//         linkActive
//       );
//     },
//     [cleanBase, currentPath],
//   );

//   const tabItems = useMemo(() => {
//     return tabs.map((tab) => {
//       const rawKey = isString(tab) ? tab.toLowerCase() : tab.path;

//       // sanitize & -> and before turning into a slug
//       const tabKey = rawKey.replace(/&/g, "and").replace(/\s+/g, "-");

//       const label = isString(tab) ? tab : tab.label;

//       const isSummary = tabKey === "summary";
//       const to = isSummary ? cleanBase : `${cleanBase}/${tabKey}`;

//       return {
//         tabKey,
//         label,
//         to,
//         isSummary,
//       };
//     });
//   }, [tabs, cleanBase]);

//   return (
//     <Box sx={styles}>
//       {tabItems.map(({ tabKey, label, to, isSummary }) => (
//         <NavLink
//           key={tabKey}
//           to={to}
//           end={!isSummary}
//           className={({ isActive: linkActive }) => {
//             const manualActive = isTabActive(tabKey, linkActive);
//             return `nav-link${manualActive ? " active" : ""}`;
//           }}
//         >
//           {label}
//         </NavLink>
//       ))}
//     </Box>
//   );
// };

// LoanDrilldownRouterTabSwitcher.propTypes = {
//   tabs: PropTypes.array.isRequired,
//   baseUrl: PropTypes.string,
//   orientation: PropTypes.oneOf(["horizontal", "vertical"]),
//   variant: PropTypes.oneOf(["background", "underline"]),
//   underlinePosition: PropTypes.oneOf(["top", "bottom", "left", "right"]),
// };

// export default LoanDrilldownRouterTabSwitcher;
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import isString from "lodash/isString";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";

import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../redux/actions/sidebarActions";
import { detailSidebarClear } from "../../../redux/reducers/detailSidebarReducer";
import { selectedStateSx } from "../../../theme/activeStates";
import { level1, slate } from "../../../theme/colors";
import { getDrilldownSubTabs } from "./drilldownSubTabs";

// How long the flyout survives the pointer leaving the tab (or the flyout).
// Long enough to cross the seam between them without a flicker, short enough
// that sweeping across the tab strip doesn't leave a menu hanging.
const FLYOUT_CLOSE_DELAY_MS = 140;

/**
 * SubTabFlyout — the hover dropdown of a tab's sub-views.
 *
 * A plain menu: anchored under the hovered tab, a card of icon + label rows,
 * one per sub-view. Labels only — the tab's own sub-header is where the counts
 * belong. Clicking a row goes straight to that view; clicking the tab itself is
 * untouched and still lands on the tab's default.
 *
 * It has to be a portalled Popper rather than an absolutely-positioned child:
 * the tab strip scrolls horizontally (`overflowX: auto`), which would clip
 * anything drawn below it.
 */
const SubTabFlyout = ({
  open,
  anchorEl,
  subTabs,
  baseUrl,
  onEnter,
  onLeave,
  onAction,
}) => (
  <Popper
    open={open}
    anchorEl={anchorEl}
    placement="bottom-start"
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    // Above the drilldown chrome but below modals/drawers (1200+) — it's a
    // transient hover surface, it should never outrank a dialog.
    style={{ zIndex: 1150 }}
    modifiers={[
      // Sits just under the tab's bottom border, left edge aligned to the tab.
      { name: "offset", options: { offset: [0, 1] } },
      // Never flip above the bar (it would cover the tab it belongs to), but do
      // shift left so the right-most tabs' menus stay on screen.
      { name: "flip", enabled: false },
      { name: "preventOverflow", options: { padding: 8 } },
    ]}
  >
    <Box
      sx={{
        minWidth: 190,
        py: 0.5,
        background: "#ffffff",
        border: `1px solid ${slate[200]}`,
        borderRadius: "8px",
        boxShadow: "0 12px 28px -12px rgba(15, 23, 42, 0.35)",
      }}
    >
      {subTabs.map(({ key, label, icon: Icon, to, sidebarType, sidebarState }) => (
        <Box
          key={key}
          // Most rows are places (a NavLink). A row carrying `sidebarType` is
          // a DOING row — "Add contact" — and opens that drilldown sidebar
          // where you already are, without first moving you to its tab.
          {...(to
            ? { component: NavLink, to: `${baseUrl}/${to}` }
            : {
                component: "button",
                type: "button",
                onClick: () => onAction?.({ sidebarType, sidebarState }),
              })}
          sx={{
            font: "inherit",
            textAlign: "left",
            background: "none",
            border: 0,
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 0.85,
            px: 1.5,
            py: 1,
            fontFamily: "var(--ibm)",
            fontSize: "11px",
            fontWeight: 400,
            lineHeight: 1.4,
            color: slate[600],
            textDecoration: "none",
            whiteSpace: "nowrap",
            "& svg": { fontSize: "14px", color: slate[400] },
            // Level 1 (place) — picking a row moves the URL, same as the tabs
            // above it. See src/theme/activeStates.js.
            "&:hover": {
              background: level1.tint,
              color: level1.text,
              fontWeight: 500,
              "& svg": { color: level1.text },
            },
          }}
        >
          {Icon && <Icon className="thin" />}
          {label}
        </Box>
      ))}
    </Box>
  </Popper>
);


const LoanDrilldownRouterTabSwitcher = ({
  tabs,
  baseUrl = "/loan/",
  orientation = "horizontal",
  variant = "background",
  underlinePosition = "bottom",
  iconPosition = "start",
  iconSize = 15,
  iconGap = 0.45,
  // Density control, not decoration: dropping the glyphs buys back ~20px a
  // tab, which is what lets the whole strip fit on a laptop. Driven by the
  // header Settings menu's "Tab icons" switch (useLoanNavIcons). The sub-tab
  // FLYOUT keeps its icons either way — it is a menu with room, not the strip.
  showIcons = true,
  // iconGap = 0.75,
  // Gates the sub-tab registry applies (currently just the Documents tab's
  // acquisitions-only Missing view).
  subTabContext,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const currentPath = useMemo(
    () => location.pathname.replace(/&/g, "and").replace(/\/$/, ""),
    [location.pathname],
  );

  const cleanBase = useMemo(
    () => baseUrl.replace(/&/g, "and").replace(/\/$/, ""),
    [baseUrl],
  );

  const styles = useMemo(() => {
    return {
      display: "flex",
      flexDirection: orientation === "horizontal" ? "row" : "column",
      ".nav-link": {
        fontFamily: "var(--ibm)",
        color: slate[600],
        fontWeight: 400,
        fontSize: "11px",
        p: 1.5,
        borderRadius: 0,
        whiteSpace: "nowrap",
        textDecoration: "none",
        transition: "all 0.2s ease",

        display: "inline-flex",
        alignItems: "center",
        gap: iconGap,

        ...(orientation === "vertical" && {
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          justifyContent: "flex-start",
          textAlign: "left",
          borderLeft: "2px solid transparent",
        }),

        ".tab-icon": {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 0,
          "& svg": {
            fontSize: iconSize,
          },
        },

        // Caret marking a tab that has sub-views. Dimmed on the active tab,
        // where hovering does nothing — you're already there, and the real
        // sub-header is on screen.
        ".tab-caret": {
          fontSize: "13px",
          ml: "-1px",
          opacity: 0.55,
          transition: "transform 0.15s ease, opacity 0.15s ease",
        },
        "&:hover .tab-caret": { opacity: 0.9 },
        "&.flyout-open .tab-caret": { transform: "rotate(180deg)", opacity: 1 },
        "&.active .tab-caret": { opacity: 0.3 },

        // These tabs move the URL, so they are level 1 (place) — teal tint,
        // teal-700 label, teal-500 indicator. See src/theme/activeStates.js.
        "&.active": {
          borderBottom: `2px solid ${level1.indicator}`,
          ...selectedStateSx({ variant, orientation, underlinePosition }),
        },
      },
    };
  }, [orientation, variant, underlinePosition, iconGap, iconSize]);

  const isTabActive = useCallback(
    (tabKey, linkActive) => {
      const isSummary = tabKey === "summary";

      if (isSummary) {
        return [cleanBase, `${cleanBase}/summary`].includes(currentPath);
      }

      const segmentRegex = new RegExp(`(^|/)${tabKey}(/|$)`);
      return (
        (currentPath.startsWith(cleanBase) &&
          segmentRegex.test(currentPath.replace(cleanBase, ""))) ||
        linkActive
      );
    },
    [cleanBase, currentPath],
  );

  const isAcquisitions = subTabContext?.isAcquisitions;
  const experienceApplies = subTabContext?.experienceApplies;
  const isConstruction = subTabContext?.isConstruction;

  const tabItems = useMemo(() => {
    return tabs.map((tab) => {
      const rawKey = isString(tab) ? tab.toLowerCase() : tab.path;

      // sanitize & -> and before turning into a slug
      const tabKey = rawKey.replace(/&/g, "and").replace(/\s+/g, "-");

      const label = isString(tab) ? tab : tab.label;

      // icon can be a component (e.g. HomeOutlined) or an element (<HomeOutlined />)
      const icon = isString(tab) ? null : tab.icon || null;

      const isSummary = tabKey === "summary";
      // Summary links to the canonical /summary path (so the URL reads
      // /summary). The bare base route still counts as active via isTabActive.
      const to = isSummary ? `${cleanBase}/summary` : `${cleanBase}/${tabKey}`;

      return {
        tabKey,
        label,
        to,
        isSummary,
        icon,
        subTabs: getDrilldownSubTabs(tabKey, {
          isAcquisitions,
          experienceApplies,
          isConstruction,
        }),
      };
    });
  }, [tabs, cleanBase, isAcquisitions, experienceApplies, isConstruction]);

  /* ── Hover flyout ─────────────────────────────────────────────────────
   * `flyout` holds the hovered tab's key plus the tab element the dropdown
   * anchors under. */
  const [flyout, setFlyout] = useState(null);
  const closeTimer = useRef(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(
      () => setFlyout(null),
      FLYOUT_CLOSE_DELAY_MS,
    );
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Any navigation (including clicking a sub-tab) dismisses the menu.
  useEffect(() => {
    cancelClose();
    setFlyout(null);
  }, [location.pathname, location.search, cancelClose]);

  const handleTabEnter = useCallback(
    (e, tabKey, subTabs) => {
      cancelClose();
      // No sub-views → nothing to preview, and sweeping onto such a tab should
      // dismiss whatever is open. A tab WITH sub-views opens its flyout even
      // while active: the sub-views are sibling pages (Applicants ↔ Experience,
      // Readiness ↔ Closing Documents), so switching between them from the tab
      // you're already on is the common case, not an edge case.
      if (!subTabs?.length) {
        setFlyout(null);
        return;
      }
      setFlyout({ tabKey, anchorEl: e.currentTarget });
    },
    [cancelClose],
  );

  const openSubTabs = useMemo(
    () => tabItems.find((t) => t.tabKey === flyout?.tabKey)?.subTabs || [],
    [tabItems, flyout],
  );

  /**
   * A flyout row that opens a drilldown sidebar rather than navigating.
   *
   * The registry declares WHICH sidebar (`sidebarType` + optional
   * `sidebarState`); dispatching it is the same three actions every tab makes,
   * so it lives here once instead of each tab teaching the nav about itself.
   */
  const handleSubTabAction = useCallback(
    ({ sidebarType, sidebarState }) => {
      if (!sidebarType) return;
      setFlyout(null);
      dispatch(detailSidebarClear());
      dispatch(sidebarTypeSet(sidebarType));
      dispatch(sidebarSetStateValue(sidebarState ?? null));
    },
    [dispatch],
  );

  const renderIcon = useCallback(
    (icon) => {
      if (!icon || !showIcons) return null;

      // If they passed an element: <HomeOutlined />
      if (icon?.$$typeof) {
        return <span className="tab-icon">{icon}</span>;
      }

      // If they passed a component: HomeOutlined
      const IconComp = icon;
      return (
        <span className="tab-icon">
          <IconComp />
        </span>
      );
    },
    [showIcons],
  );

  return (
    <>
      <Box sx={styles}>
        {tabItems.map(({ tabKey, label, to, isSummary, icon, subTabs }) => (
          <NavLink
            key={tabKey}
            to={to}
            end={!isSummary}
            onMouseEnter={(e) => handleTabEnter(e, tabKey, subTabs)}
            onMouseLeave={scheduleClose}
            className={({ isActive: linkActive }) => {
              const manualActive = isTabActive(tabKey, linkActive);
              return `nav-link${manualActive ? " active" : ""}${
                flyout?.tabKey === tabKey ? " flyout-open" : ""
              }`;
            }}
          >
            {iconPosition === "start" && renderIcon(icon)}
            <span>{label}</span>
            {iconPosition === "end" && renderIcon(icon)}
            {!!subTabs?.length && (
              <KeyboardArrowDownRounded className="tab-caret" />
            )}
          </NavLink>
        ))}
      </Box>
      <SubTabFlyout
        open={!!flyout && openSubTabs.length > 0}
        anchorEl={flyout?.anchorEl || null}
        subTabs={openSubTabs}
        baseUrl={cleanBase}
        onEnter={cancelClose}
        onLeave={scheduleClose}
        onAction={handleSubTabAction}
      />
    </>
  );
};

LoanDrilldownRouterTabSwitcher.propTypes = {
  tabs: PropTypes.array.isRequired,
  baseUrl: PropTypes.string,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  variant: PropTypes.oneOf(["background", "underline"]),
  underlinePosition: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  iconPosition: PropTypes.oneOf(["start", "end"]),
  iconSize: PropTypes.number,
  iconGap: PropTypes.number,
  showIcons: PropTypes.bool,
  subTabContext: PropTypes.shape({
    isAcquisitions: PropTypes.bool,
    experienceApplies: PropTypes.bool,
    isConstruction: PropTypes.bool,
  }),
};

export default LoanDrilldownRouterTabSwitcher;
